import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WhatsAppConfigList } from '@/components/whatsapp/WhatsAppConfigList';
import { WhatsAppConfigForm } from '@/components/whatsapp/WhatsAppConfigForm';
import {
  useWhatsAppConfigs,
  useCreateWhatsAppConfig,
  useUpdateWhatsAppConfig,
} from '@/hooks/use-whatsapp-config';
import type { WhatsAppConfig } from '@/types';
import type { WhatsAppConfigFormData } from '@/schemas/whatsapp-config.schema';

export default function WhatsAppConfigPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingConfig, setEditingConfig] = useState<WhatsAppConfig | undefined>();

  const { data: configs, isLoading, isError, error } = useWhatsAppConfigs();
  const createMutation = useCreateWhatsAppConfig();
  const updateMutation = useUpdateWhatsAppConfig();

  const handleCreate = () => {
    setFormMode('create');
    setEditingConfig(undefined);
    setFormOpen(true);
  };

  const handleEdit = (config: WhatsAppConfig) => {
    setFormMode('edit');
    setEditingConfig(config);
    setFormOpen(true);
  };

  const handleSubmit = (data: WhatsAppConfigFormData) => {
    if (formMode === 'create') {
      createMutation.mutate(data, {
        onSuccess: () => setFormOpen(false),
      });
    } else if (editingConfig?.id) {
      updateMutation.mutate(
        { id: editingConfig.id, data },
        { onSuccess: () => setFormOpen(false) },
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12 text-destructive">
        Failed to load configurations: {error?.message ?? 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">WhatsApp Configuration</h1>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Create Configuration
        </Button>
      </div>

      <WhatsAppConfigList configs={Array.isArray(configs) ? configs : []} onEdit={handleEdit} />

      <WhatsAppConfigForm
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        initialData={editingConfig}
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}
